







































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import moment from "moment";
import { Chart } from "highcharts-vue";
import dataQualityClient from "@/Scripts/clients/data-quality-client.js";

interface OutlierTimelinePoint {
  timeStamp: string | Date;
  value: number;
  isOutlier: boolean;
  reasonCode?: string;
  isAcknowledged?: boolean;
  isDeleted?: boolean;
  reviewedByName?: string;
}

@Component({
  components: {
    Highcharts: Chart,
  },
})
export default class VesselFeatureChart extends Vue {
  @Prop({ required: true, type: String }) featureName!: string;
  @Prop({ required: true, type: Number }) vesselId!: number;
  @Prop({ required: true, type: String }) vesselName!: string;
  @Prop({ required: true, type: String }) fromDate!: string;
  @Prop({ required: true, type: String }) toDate!: string;
  @Prop({ type: Boolean, default: true }) loadImmediately!: boolean;
  @Prop({ type: Number, default: null }) chartIndex!: number;

  private loading = false;
  private error = false;
  private chart: any = null;
  private chartLoaded = false;
  private timelineData: OutlierTimelinePoint[] = [];
  private showRemovedOutliers = false;

  get hasData(): boolean {
    return this.timelineData.length > 0;
  }

  get chartOptions() {
    if (!this.chartLoaded || !this.hasData) {
      return {
        chart: this.chartSettings,
        title: { text: null },
        series: [],
      };
    }

    // Valid data points (not outliers)
    const normalPoints = this.timelineData
      .filter(point => !point.isOutlier && point.value !== undefined)
      .map(point => [new Date(point.timeStamp).getTime(), point.value]);

    // Acknowledged outliers (user marked as good data)
    const acknowledgedPoints = this.timelineData
      .filter(point => point.isOutlier && point.value !== undefined && point.isAcknowledged && !point.isDeleted)
      .map(point => ({ x: new Date(point.timeStamp).getTime(), y: point.value, reviewedByName: point.reviewedByName }));

    // Unmanaged physical bounds outliers
    const physicalBoundsOutliers = this.timelineData
      .filter(point => point.isOutlier && point.value !== undefined && !point.isAcknowledged && !point.isDeleted && point.reasonCode === "PhysicalBounds")
      .map(point => [new Date(point.timeStamp).getTime(), point.value]);

    // Unmanaged smart outliers
    const modelledBoundsOutliers = this.timelineData
      .filter(point => point.isOutlier && point.value !== undefined && !point.isAcknowledged && !point.isDeleted && point.reasonCode === "SmartOutliers")
      .map(point => [new Date(point.timeStamp).getTime(), point.value]);

    // Removed outliers (hidden by default) — use simple arrays for performance
    const removedOutliers = this.timelineData
      .filter(point => point.isOutlier && point.value !== undefined && point.isDeleted)
      .map(point => [new Date(point.timeStamp).getTime(), point.value]);

    return {
      chart: this.chartSettings,
      title: { text: null },
      xAxis: {
        type: "datetime",
        title: { text: "Time" },
        minRange: 24 * 3600 * 1000, // 1 day minimum range
        labels: { style: { color: this.themeColor("ContentPrimary") } },
        lineColor: this.themeColor("ContentInverseSecondary"),
        minorTickLength: 0,
        tickLength: 0,
      },
      yAxis: {
        title: { text: this.featureName },
        labels: { style: { color: this.themeColor("ContentPrimary") } },
        gridLineColor: this.themeColor("ContentInverseSecondary"),
      },
      legend: {
        enabled: true,
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          fontSize: "14px",
          color: this.themeColor("ContentTertiary"),
          fontWeight: "400",
        },
        itemHoverStyle: {
          color: this.themeColor("ContentTertiary"),
          fontWeight: "500",
        },
      },
      tooltip: {
        shared: false,
        crosshairs: true,
        useHTML: true,
        backgroundColor: "#000",
        borderWidth: 0,
        borderRadius: 8,
        shadow: false,
        style: {
          color: "#fff",
          fontSize: 12,
        },
        formatter: function (this: any) {
          const reviewedBy = this.point.reviewedByName
            ? `<br/><span style="font-size: 11px; opacity: 0.8;">Reviewed by: ${this.point.reviewedByName}</span>`
            : "";
          return `<div style="line-height: 18px; padding: 5px;">
                        <span style="font-size: 12px; margin-bottom: 3px;">${moment(this.x).format("MMM DD, YYYY HH:mm")}</span><br/>
                        <span style="color:${this.color}">●</span> <span style="font-size: 12px;">${this.series.name}: <b>${this.y.toFixed(2)}</b></span>${reviewedBy}
                      </div>`;
        },
      },
      plotOptions: {
        scatter: {
          marker: {
            states: {
              hover: {
                enabled: true,
                radius: 4,
              },
            },
          },
          cropThreshold: 9999,
          enableMouseTracking: true,
        },
      },
      credits: { enabled: false },
      exporting: { enabled: false },
      series: [
        {
          name: "Valid Values",
          type: "scatter",
          data: normalPoints,
          color: "#4caf50",
          marker: { radius: 2 },
          zIndex: 1,
          showInLegend: normalPoints.length > 0,
        },
        {
          name: "Acknowledged",
          type: "scatter",
          data: acknowledgedPoints,
          color: "#2e7d32",
          marker: { radius: 3, symbol: "diamond" },
          zIndex: 2,
          showInLegend: acknowledgedPoints.length > 0,
        },
        {
          name: "Physical Bounds Outliers",
          type: "scatter",
          data: physicalBoundsOutliers,
          color: "#9c27b0",
          marker: { radius: 2.5 },
          zIndex: 4,
          showInLegend: physicalBoundsOutliers.length > 0,
        },
        {
          name: "Smart Outliers",
          type: "scatter",
          data: modelledBoundsOutliers,
          color: "#2196f3",
          marker: { radius: 2.5 },
          zIndex: 3,
          showInLegend: modelledBoundsOutliers.length > 0,
        },
        {
          name: "Removed",
          type: "scatter",
          data: removedOutliers,
          color: "#9e9e9e",
          marker: { radius: 2.5, symbol: "cross" },
          zIndex: 5,
          visible: false,
          showInLegend: removedOutliers.length > 0,
          animation: false,
        },
      ],
    };
  }

  get chartSettings(): any {
    if (!this.chartLoaded) return {};
    const ctx = this;
    return {
      type: "scatter",
      zoomType: "x",
      spacingRight: 20,
      spacingLeft: 20,
      spacingTop: 28,
      spacingBottom: 10,
      height: 300,
      backgroundColor: this.themeColor("BackgroundPrimaryDefault"),
      style: { fontFamily: "Helvetica Neue" },
      events: {
        selection: function (e: any) {
          const $this: any = this;
          setTimeout(() => {
            ctx.$emit("onZoom", {
              from: $this.xAxis[0].min,
              to: $this.xAxis[0].max,
              resetZoom: Boolean(e.resetSelection),
            });
            return false;
          }, 100);
        },
      },
    };
  }

  @Watch("vesselId")
  @Watch("featureName")
  @Watch("fromDate")
  @Watch("toDate")
  onPropsChange() {
    // Reset state when key props change
    this.resetComponent();
  }

  @Watch("loadImmediately")
  onLoadImmediatelyChange(newVal: boolean) {
    if (newVal && !this.loading) {
      this.startLoading();
    }
  }

  mounted() {
    if (this.loadImmediately) {
      this.startLoading();
    }
  }

  resetComponent() {
    this.loading = false;
    this.error = false;
    this.chartLoaded = false;
    this.timelineData = [];
    this.chart = null;
    this.showRemovedOutliers = false;
  }

  async startLoading() {
    this.loading = true;
    this.error = false;
    this.chartLoaded = false;
    this.timelineData = [];

    try {
      const response = await dataQualityClient.getOutlierTimeline({
        vesselId: this.vesselId,
        featureName: this.featureName,
        from: this.fromDate,
        to: this.toDate,
      });

      this.timelineData = response || [];

      // Enable chart after data is loaded
      if (this.timelineData.length > 0 && this.chart) {
        this.$nextTick(() => {
          if (this.chart) {
            this.chart.update(this.chartOptions, true);
          }
          this.chartLoaded = true;
          this.$emit("onChartLoaded", this.featureName);
        });
      } else {
        this.chartLoaded = true;
        this.$emit("onChartLoaded", this.featureName);
      }
    } catch (error) {
      // Failed to load timeline data
      this.error = true;
      this.$emit("onChartError", this.featureName);
    } finally {
      this.loading = false;
    }
  }

  chartReady(chart: any): void {
    this.chart = chart;
    if (this.hasData) {
      this.chart.update(this.chartOptions, true);
    }
    this.chartLoaded = true;
  }

  retryLoad(): void {
    this.startLoading();
  }

  toggleRemovedSeries(): void {
    if (!this.chart) return;
    const removedSeries = this.chart.series.find((s: any) => s.name === "Removed");
    if (removedSeries) {
      removedSeries.setVisible(this.showRemovedOutliers, false);
      this.chart.redraw(false);
    }
  }

  formatDateRange(from: string, to: string): string {
    return `${moment(from).format("MMM DD, YYYY")} - ${moment(to).format("MMM DD, YYYY")}`;
  }

  themeColor(color: string): string {
    return (this.$vuetify.theme.themes[this.$vuetify.theme.dark ? "dark" : "light"][color] as any) ?? this.$vuetify.theme.themes.light[color];
  }
}
